import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PARK_URL } from "@/lib/assets";
import { CalendarDays, MapPin, Ticket, Lock, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const dateFmt = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";

function ReplayCountdown({ expiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - now;
  if (diff <= 0) return <div className="text-xs text-burgundy" data-testid="replay-expired">Replay window has closed.</div>;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  return (
    <div className="text-xs text-plum flex items-center gap-1" data-testid="replay-countdown">
      <PlayCircle className="w-3.5 h-3.5" /> Replay expires in {days}d {hours}h {mins}m
    </div>
  );
}

export function EventsList() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events").then(r => setEvents(r.data)); }, []);
  return (
    <div className="bg-cream">
      <div className="brand-container py-20">
        <div className="brand-eyebrow">Live & Upcoming</div>
        <h1 className="brand-h1 mt-3 mb-4">Take a seat.<br/>Auntie's live.</h1>
        <p className="brand-lede max-w-2xl">Firesides, private sessions, and ticketed specials. Register with a free account for community events, or grab a ticket for the featured live.</p>
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          {events.map((e) => (
            <Link key={e.id} to={`/events/${e.id}`} className="editorial-card overflow-hidden" data-testid={`events-card-${e.id}`}>
              <img src={e.cover_image || PARK_URL} alt="" className="w-full aspect-[16/9] object-cover" />
              <div className="p-6">
                <div className="brand-eyebrow">
                  {e.access_level === "purchase" ? `Ticket · $${(e.price||0).toFixed(2)}` : e.access_level === "paid" ? "Members Only" : e.access_level === "free" ? "Free · Register" : "Public"}
                </div>
                <h3 className="mt-3 font-serif text-2xl leading-tight text-charcoal">{e.title}</h3>
                <div className="mt-4 space-y-1 text-sm text-charcoal/60">
                  <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4" /> {dateFmt(e.starts_at)}</div>
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {e.location}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EventDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => api.get(`/events/${id}`).then(r => setE(r.data)).catch(() => setE(false));
  useEffect(() => { load(); }, [id]);

  const register = async () => {
    if (!user) return nav(`/login?next=/events/${id}`);
    setBusy(true);
    try {
      await api.post(`/events/${id}/register`);
      toast.success("You're registered. Auntie will see you there.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const buy = async () => {
    if (!user) return nav(`/login?next=/events/${id}`);
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", {
        kind: "event", ref_id: id, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  if (e === false) return <div className="brand-container py-20 text-charcoal/60">Event not found.</div>;
  if (!e) return <div className="brand-container py-20 text-charcoal/40">Loading…</div>;

  return (
    <div className="bg-cream">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <Link to="/events" className="text-sm text-plum hover:underline" data-testid="event-back-link">← Back to events</Link>
        <div className="grid lg:grid-cols-12 gap-10 mt-8">
          <div className="lg:col-span-7">
            <img src={e.cover_image || PARK_URL} alt="" className="w-full aspect-video rounded-lg object-cover border border-gold-light" />
            <div className="brand-eyebrow mt-8">Event</div>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-tight mt-3">{e.title}</h1>
            <p className="mt-6 text-charcoal/80 whitespace-pre-line">{e.description}</p>
          </div>
          <aside className="lg:col-span-5">
            <div className="editorial-card p-6 sticky top-24">
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2 text-charcoal/70"><CalendarDays className="w-4 h-4" /> {dateFmt(e.starts_at)}</div>
                <div className="flex items-center gap-2 text-charcoal/70"><MapPin className="w-4 h-4" /> {e.location}</div>
                <div className="flex items-center gap-2 text-charcoal/70"><Ticket className="w-4 h-4" /> {e.access_level === "purchase" ? `$${(e.price||0).toFixed(2)}` : e.access_level === "paid" ? "Members-only" : "Free"}</div>
              </div>
              <div className="h-px bg-gold-light my-5" />
              {e.access_level === "purchase" ? (
                <button disabled={busy} onClick={buy} className="btn-primary w-full" data-testid="event-buy-btn">{busy ? "…" : `Buy ticket · $${(e.price||0).toFixed(2)}`}</button>
              ) : e.access_level === "paid" ? (
                e.can_access ? (
                  <div className="text-plum font-medium">Access included with your membership.</div>
                ) : (
                  <Link to="/membership" className="btn-primary w-full block text-center" data-testid="event-membership-btn">Enter the Chambers</Link>
                )
              ) : (
                <button disabled={busy} onClick={register} className="btn-primary w-full" data-testid="event-register-btn">{busy ? "…" : "Register free"}</button>
              )}
              {e.can_access && e.livestream_url && !e.replay_url && (
                <a href={e.livestream_url} target="_blank" rel="noreferrer" className="btn-outline w-full mt-3 text-center block" data-testid="event-open-stream">Open livestream</a>
              )}
              {e.can_access && e.replay_url && (
                <>
                  <a href={e.replay_url} target="_blank" rel="noreferrer" className="btn-outline w-full mt-3 text-center block" data-testid="event-open-replay">Watch replay</a>
                  <div className="mt-3">
                    <ReplayCountdown expiresAt={e.replay_expires_at} />
                  </div>
                </>
              )}
              {!e.can_access && (e.access_level === "paid" || e.access_level === "purchase") && (
                <div className="mt-4 text-xs text-charcoal/50 flex items-start gap-2"><Lock className="w-3.5 h-3.5 mt-0.5" /> Livestream link is revealed after access is granted.</div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
